import * as exifr from "exifr";

export async function normalizeImageFile(file: File): Promise<File> {
  // Get EXIF data including dimensions
  const exifData = await exifr.parse(file).catch(() => ({}));
  const orientation = exifData.Orientation ?? 1;
  const exifWidth = exifData.ImageWidth || exifData.PixelXDimension;
  const exifHeight = exifData.ImageHeight || exifData.PixelYDimension;

  console.log('EXIF Data:', {
    orientation,
    exifWidth,
    exifHeight,
    exifAspectRatio: exifWidth && exifHeight ? (exifWidth / exifHeight).toFixed(3) : 'unknown'
  });

  // If already normal (orientation = 1), return original
  if (orientation === 1) return file;

  // Apply the inverse EXIF transformation to correct the image pixels
  const img = new Image();
  const imageUrl = URL.createObjectURL(file);

  await new Promise((resolve, reject) => {
    img.onload = resolve;
    img.onerror = reject;
    img.src = imageUrl;
  });

  // Log actual image dimensions and aspect ratio
  const actualWidth = img.width;
  const actualHeight = img.height;
  const actualAspectRatio = (actualWidth / actualHeight).toFixed(3);

  console.log('Actual Image Data:', {
    actualWidth,
    actualHeight,
    actualAspectRatio,
    dimensionsMatchExif: exifWidth === actualWidth && exifHeight === actualHeight,
    aspectRatioMatch: exifWidth && exifHeight ? Math.abs((exifWidth / exifHeight) - (actualWidth / actualHeight)) < 0.001 : false
  });

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d')!;

  // Set canvas dimensions based on orientation
  let canvasWidth = img.width;
  let canvasHeight = img.height;

  // Apply inverse transformations to correct the orientation
  switch (orientation) {
    case 2: // Mirror horizontal
      ctx.scale(-1, 1);
      ctx.translate(-canvasWidth, 0);
      break;
    case 3: // 180° rotation
      ctx.rotate(Math.PI);
      ctx.translate(-canvasWidth, -canvasHeight);
      break;
    case 4: // Mirror vertical
      ctx.scale(1, -1);
      ctx.translate(0, -canvasHeight);
      break;
    case 5: // Mirror horizontal + 90° clockwise
      canvasWidth = img.height;
      canvasHeight = img.width;
      ctx.rotate(Math.PI / 2);
      ctx.scale(1, -1);
      ctx.translate(0, -canvasHeight);
      break;
    case 6: // 90° clockwise
      canvasWidth = img.height;
      canvasHeight = img.width;
      ctx.translate(canvasWidth, 0);
      ctx.rotate(Math.PI / 2);
      break;
    case 7: // Mirror vertical + 90° clockwise
      canvasWidth = img.height;
      canvasHeight = img.width;
      ctx.rotate(Math.PI / 2);
      ctx.translate(0, -canvasWidth);
      ctx.scale(-1, 1);
      break;
    case 8: // 90° counter-clockwise
      canvasWidth = img.height;
      canvasHeight = img.width;
      ctx.translate(0, canvasHeight);
      ctx.rotate(-Math.PI / 2);
      break;
  }

  canvas.width = canvasWidth;
  canvas.height = canvasHeight;
  ctx.drawImage(img, 0, 0);

  URL.revokeObjectURL(imageUrl);

  // Convert to blob without EXIF data
  const blob = await new Promise<Blob>((resolve) => {
    canvas.toBlob((blob) => resolve(blob!), 'image/jpeg', 0.95);
  });

  console.log(`Corrected image orientation:`, {
    originalOrientation: orientation,
    originalDimensions: `${actualWidth}x${actualHeight}`,
    originalAspectRatio: actualAspectRatio,
    correctedDimensions: `${canvasWidth}x${canvasHeight}`,
    correctedAspectRatio: (canvasWidth / canvasHeight).toFixed(3),
    dimensionsChanged: actualWidth !== canvasWidth || actualHeight !== canvasHeight
  });

  return new File([blob], file.name.replace(/\.(jpe?g|png|heic)$/i, '.jpg'), {
    type: 'image/jpeg',
    lastModified: Date.now()
  });
}
